import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { invalidateVersionHistoryQueries } from "./versionQueryUtils";
import { SmartCell, SmartColumn, unwrapSmartTableEntityPayload } from "./types";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";

type ImportAnchorCellPayload = { request_log_id: number } | { trace_id: string };

interface ImportAnchorCellArgs {
  tableId: string;
  sheetId: string;
  cellId: string;
  payload: ImportAnchorCellPayload;
}

export interface ImportAnchorCellResult {
  cell: SmartCell;
  columns: SmartColumn[];
}

export const useImportAnchorCell = () => {
  const userToken = useAuth()?.userToken ?? "";
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const wid = Number(workspaceId);
  const queryClient = useQueryClient();

  return useMutation<ImportAnchorCellResult, unknown, ImportAnchorCellArgs>({
    mutationFn: async ({ tableId, sheetId, cellId, payload }) => {
      const response = await fetch(
        ENDPOINTS.smart_table_cell_import(wid, tableId, sheetId, cellId),
        {
          method: "POST",
          headers: jsonAuthHeaders(userToken),
          body: JSON.stringify(payload),
        },
      );
      const responsePayload = await handleResponse(response);
      const cell = unwrapSmartTableEntityPayload<SmartCell>(responsePayload, "cell");
      const columns = Array.isArray(responsePayload?.columns)
        ? (responsePayload.columns as SmartColumn[])
        : [];
      return { cell, columns };
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          "smart-tables",
          wid,
          variables.tableId,
          variables.sheetId,
          "cells",
          variables.cellId,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: ["smart-tables", wid, variables.tableId, variables.sheetId, "columns"],
      });
      invalidateVersionHistoryQueries(queryClient, wid, variables.tableId, variables.sheetId);
    },
  });
};
